import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Send, Trash2, AlertTriangle, BarChart3, RefreshCw,
  Search, ChevronLeft, ChevronRight, Eye, X, Shield, Inbox, CheckCircle,
  Reply, ArrowLeft
} from '../icons';
import { useAuth } from '../context/AuthContext';
import {
  getSESMetrics, getSESEmails, sendSESEmail, replySESEmail,
  deleteSESEmail, toggleSESSpam, bulkDeleteSESEmails, getSESIdentities
} from '../services/api';
import { useConfirm } from '../components/Modal';

function SESMailbox() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [activeView, setActiveView] = useState('metrics'); // metrics, inbox, compose
  const [metrics, setMetrics] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailTotal, setEmailTotal] = useState(0);
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewEmail, setViewEmail] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [composeForm, setComposeForm] = useState({ to: '', subject: '', body: '' });

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    loadMetrics();
  }, [user, navigate, authLoading]);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const [metricsRes, identitiesRes] = await Promise.all([
        getSESMetrics(),
        getSESIdentities().catch(() => ({ data: { identities: [] } })),
      ]);
      setMetrics(metricsRes.data);
      setIdentities(identitiesRes.data?.identities || []);
    } catch (err) {
      console.error('Failed to load SES data:', err);
    } finally { setLoading(false); }
  };

  const loadEmails = useCallback(async () => {
    setEmailLoading(true);
    try {
      const res = await getSESEmails({ status: statusFilter || undefined, search: searchQuery || undefined });
      setEmails(res.data.emails || []);
      setEmailTotal(res.data.total || 0);
    } catch { setEmails([]); }
    finally { setEmailLoading(false); }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    if (activeView === 'inbox') loadEmails();
  }, [activeView, loadEmails]);

  const handleSend = async () => {
    if (!composeForm.to || !composeForm.subject || !composeForm.body) return;
    setSendLoading(true);
    setSendResult(null);
    try {
      await sendSESEmail(composeForm);
      setSendResult({ type: 'success', text: 'Email sent successfully!' });
      setComposeForm({ to: '', subject: '', body: '' });
    } catch (err) {
      setSendResult({ type: 'error', text: err.response?.data?.error || err.message });
    } finally { setSendLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog({ title: 'Delete Email', message: 'Delete this email log entry?', danger: true, confirmText: 'Delete' })) return;
    try {
      await deleteSESEmail(id);
      setEmails(prev => prev.filter(e => e.id !== id));
      setViewEmail(null);
    } catch { alert('Failed to delete'); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!await confirmDialog({ title: 'Bulk Delete', message: `Delete ${selectedIds.size} selected email(s)?`, danger: true, confirmText: 'Delete All' })) return;
    try {
      await bulkDeleteSESEmails([...selectedIds]);
      setEmails(prev => prev.filter(e => !selectedIds.has(e.id)));
      setSelectedIds(new Set());
    } catch { alert('Failed to delete'); }
  };

  const handleToggleSpam = async (id, current) => {
    try {
      await toggleSESSpam(id, !current);
      setEmails(prev => prev.map(e => e.id === id ? { ...e, is_spam: !current } : e));
      if (viewEmail?.id === id) setViewEmail(prev => ({ ...prev, is_spam: !current }));
    } catch { alert('Failed to update spam status'); }
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  const ses = metrics?.ses;
  const quota = metrics?.quota;
  const dbLog = metrics?.dbLog;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
          <ChevronLeft size={16} /> Admin
        </button>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0, color: '#1f2937' }}>
          <Mail size={28} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          AWS SES Mailbox
        </h1>
        <button onClick={loadMetrics} style={{ marginLeft: 'auto', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Tab navigation */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
        {[
          { key: 'metrics', label: 'Metrics & Stats', icon: <BarChart3 size={14} /> },
          { key: 'inbox', label: `Sent Emails (${emailTotal})`, icon: <Inbox size={14} /> },
          { key: 'compose', label: 'Compose', icon: <Send size={14} /> },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveView(tab.key)}
            style={{
              padding: '10px 18px', border: 'none',
              background: activeView === tab.key ? '#fff' : 'transparent',
              borderBottom: activeView === tab.key ? '3px solid #DC143C' : '3px solid transparent',
              color: activeView === tab.key ? '#DC143C' : '#6b7280',
              fontWeight: activeView === tab.key ? 600 : 400,
              cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px',
              marginBottom: '-2px',
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ============================================================ */}
      {/* METRICS VIEW */}
      {/* ============================================================ */}
      {activeView === 'metrics' && (
        <div>
          {/* SES Metrics Cards */}
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 12px' }}>
            {ses ? 'AWS SES Sending Statistics' : 'Email Sending Statistics'}
            {!ses && <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 400, marginLeft: '8px' }}>(AWS API not connected — showing DB log)</span>}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            <MetricCard label="Sent" value={ses?.sent ?? dbLog?.total ?? '-'} color="#3b82f6" />
            <MetricCard label="Delivered" value={ses?.delivered ?? dbLog?.delivered_count ?? '-'} sub={ses ? `${ses.deliveredRate}%` : ''} color="#16a34a" />
            <MetricCard label="Complaints" value={ses?.complaints ?? dbLog?.complained_count ?? '-'} sub={ses ? `${ses.complaintRate}%` : ''} color="#f59e0b" />
            <MetricCard label="Bounces" value={ses?.bounces ?? dbLog?.bounced_count ?? '-'} sub={ses ? `${ses.bounceRate}%` : ''} color="#dc2626" />
            <MetricCard label="Opens" value={ses?.opens ?? '-'} sub={ses?.openRate ? `${ses.openRate}%` : ''} color="#8b5cf6" />
            <MetricCard label="Clicks" value={ses?.clicks ?? '-'} color="#06b6d4" />
          </div>

          {/* SES Account / SMTP Info */}
          {quota && (
            <>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 12px' }}>SES Account</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                <div className="detail-card" style={{ padding: '16px' }}>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Sent Last 24h</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>{quota.sentLast24Hours ?? '-'}</div>
                </div>
                {quota.max24HourSend != null && (
                  <div className="detail-card" style={{ padding: '16px' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>24-Hour Send Limit</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>{quota.max24HourSend}</div>
                  </div>
                )}
                {quota.maxSendRate != null && (
                  <div className="detail-card" style={{ padding: '16px' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>Max Send Rate</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>{quota.maxSendRate}/sec</div>
                  </div>
                )}
                {quota.productionAccess != null && (
                  <div className="detail-card" style={{ padding: '16px' }}>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>Production Access</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: quota.productionAccess ? '#16a34a' : '#f59e0b' }}>
                      {quota.productionAccess ? 'Yes' : 'Sandbox'}
                    </div>
                  </div>
                )}
                <div className="detail-card" style={{ padding: '16px' }}>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>SMTP Host</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937', wordBreak: 'break-all' }}>{quota.smtpHost || '-'}</div>
                </div>
                <div className="detail-card" style={{ padding: '16px' }}>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>From Address</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937', wordBreak: 'break-all' }}>{quota.fromEmail || '-'}</div>
                </div>
                <div className="detail-card" style={{ padding: '16px' }}>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Status</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: (quota.sendingEnabled !== false) ? '#16a34a' : '#f59e0b' }}>
                    {(quota.sendingEnabled !== false) ? 'Active' : 'Disabled'}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* DB Log Summary */}
          {dbLog && (
            <>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 12px' }}>Internal Email Log</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                <MetricCard label="Total Logged" value={dbLog.total} color="#374151" />
                <MetricCard label="Sent" value={dbLog.sent_count} color="#3b82f6" />
                <MetricCard label="Delivered" value={dbLog.delivered_count} color="#16a34a" />
                <MetricCard label="Bounced" value={dbLog.bounced_count} color="#dc2626" />
                <MetricCard label="Complained" value={dbLog.complained_count} color="#f59e0b" />
                <MetricCard label="Spam" value={dbLog.spam_count} color="#9333ea" />
              </div>
            </>
          )}

          {/* Verified Identities */}
          {identities.length > 0 && (
            <>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 12px' }}>Verified Identities</h3>
              <div className="detail-card" style={{ padding: '16px' }}>
                {identities.map((id, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: i < identities.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <Shield size={14} style={{ color: id.status === 'Success' ? '#16a34a' : '#f59e0b' }} />
                    <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>{id.identity}</span>
                    <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: id.status === 'Success' ? '#DCFCE7' : '#FEF3C7', color: id.status === 'Success' ? '#166534' : '#92400E' }}>
                      {id.status}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Data Points Chart (simple bar chart) */}
          {metrics?.dataPoints?.length > 0 && (
            <>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '24px 0 12px' }}>Recent Activity (last 14 data points)</h3>
              <div className="detail-card" style={{ padding: '16px', overflowX: 'auto' }}>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-end', minHeight: '120px' }}>
                  {metrics.dataPoints.map((dp, i) => {
                    const max = Math.max(...metrics.dataPoints.map(d => d.attempts || 1));
                    const h = Math.max(((dp.attempts || 0) / max) * 100, 4);
                    return (
                      <div key={i} style={{ flex: 1, minWidth: '30px', textAlign: 'center' }}>
                        <div style={{ height: `${h}px`, background: dp.bounces > 0 ? '#FCA5A5' : '#93C5FD', borderRadius: '4px 4px 0 0', position: 'relative' }} title={`${dp.attempts} sent, ${dp.bounces} bounces`}>
                          {dp.bounces > 0 && (
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${Math.max((dp.bounces / (dp.attempts || 1)) * 100, 10)}%`, background: '#EF4444', borderRadius: '0 0 0 0' }} />
                          )}
                        </div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>
                          {new Date(dp.timestamp).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
                  <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#93C5FD', borderRadius: '2px', marginRight: '4px' }} />Sent</span>
                  <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#EF4444', borderRadius: '2px', marginRight: '4px' }} />Bounces</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* INBOX (Sent Emails Log) */}
      {/* ============================================================ */}
      {activeView === 'inbox' && (
        <div>
          {/* Search & Filters */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 200px' }}>
              <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text" placeholder="Search emails..."
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
              <option value="">All Status</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="bounced">Bounced</option>
              <option value="complained">Complained</option>
              <option value="failed">Failed</option>
            </select>
            <button onClick={loadEmails} style={{ padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
              <RefreshCw size={14} /> Refresh
            </button>
            {selectedIds.size > 0 && (
              <button onClick={handleBulkDelete} style={{ padding: '8px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', cursor: 'pointer', color: '#DC2626', fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Trash2 size={14} /> Delete ({selectedIds.size})
              </button>
            )}
          </div>

          {/* Email List */}
          {emailLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" /></div>
          ) : emails.length === 0 ? (
            <div className="detail-card" style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              <Mail size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
              <p>No emails found.</p>
            </div>
          ) : (
            <div className="detail-card" style={{ overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', width: '30px' }}>
                      <input type="checkbox"
                        checked={selectedIds.size === emails.length && emails.length > 0}
                        onChange={(e) => setSelectedIds(e.target.checked ? new Set(emails.map(em => em.id)) : new Set())} />
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>To</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Subject</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Date</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280', fontWeight: 600, width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map(em => (
                    <tr key={em.id} style={{ borderBottom: '1px solid #f3f4f6', background: em.is_spam ? '#FEF2F2' : 'transparent' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <input type="checkbox" checked={selectedIds.has(em.id)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            e.target.checked ? next.add(em.id) : next.delete(em.id);
                            setSelectedIds(next);
                          }} />
                      </td>
                      <td style={{ padding: '10px 12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {em.recipient}
                      </td>
                      <td style={{ padding: '10px 12px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <button onClick={() => setViewEmail(em)} style={{ background: 'none', border: 'none', color: '#1f2937', cursor: 'pointer', textAlign: 'left', padding: 0, fontWeight: 500 }}>
                          {em.subject || '(no subject)'}
                          {em.is_spam && <span style={{ marginLeft: '6px', fontSize: '11px', background: '#FCA5A5', color: '#991B1B', padding: '1px 6px', borderRadius: '4px' }}>SPAM</span>}
                        </button>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <StatusBadge status={em.status} />
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {new Date(em.created_at).toLocaleDateString()} {new Date(em.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button onClick={() => setViewEmail(em)} title="View" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', padding: '4px' }}><Eye size={16} /></button>
                          <button onClick={() => handleToggleSpam(em.id, em.is_spam)} title={em.is_spam ? 'Not Spam' : 'Mark Spam'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: em.is_spam ? '#16a34a' : '#f59e0b', padding: '4px' }}>
                            {em.is_spam ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                          </button>
                          <button onClick={() => handleDelete(em.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px' }}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Email Detail Modal */}
          {viewEmail && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
              onClick={() => setViewEmail(null)}>
              <div style={{ background: '#fff', borderRadius: '12px', maxWidth: '600px', width: '100%', maxHeight: '80vh', overflow: 'auto', padding: '24px' }}
                onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>Email Details</h3>
                  <button onClick={() => setViewEmail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
                </div>
                <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.8 }}>
                  <p><strong>To:</strong> {viewEmail.recipient}</p>
                  <p><strong>Subject:</strong> {viewEmail.subject}</p>
                  <p><strong>Status:</strong> <StatusBadge status={viewEmail.status} /></p>
                  <p><strong>Sent:</strong> {new Date(viewEmail.created_at).toLocaleString()}</p>
                  {viewEmail.message_id && <p><strong>Message ID:</strong> <code style={{ fontSize: '12px', wordBreak: 'break-all' }}>{viewEmail.message_id}</code></p>}
                  <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
                  <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {viewEmail.body || '(no body)'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                  <button onClick={() => handleToggleSpam(viewEmail.id, viewEmail.is_spam)}
                    style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: '8px', background: viewEmail.is_spam ? '#DCFCE7' : '#FEF3C7', color: viewEmail.is_spam ? '#166534' : '#92400E', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                    {viewEmail.is_spam ? 'Mark Not Spam' : 'Mark as Spam'}
                  </button>
                  <button onClick={() => handleDelete(viewEmail.id)}
                    style={{ padding: '8px 14px', border: '1px solid #FECACA', borderRadius: '8px', background: '#FEF2F2', color: '#DC2626', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* COMPOSE VIEW */}
      {/* ============================================================ */}
      {activeView === 'compose' && (
        <div className="detail-card" style={{ padding: '24px', maxWidth: '700px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1f2937' }}>
            <Send size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Compose Email
          </h3>

          {sendResult && (
            <div style={{
              padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px',
              background: sendResult.type === 'success' ? '#DCFCE7' : '#FEF2F2',
              color: sendResult.type === 'success' ? '#166534' : '#991B1B',
              border: `1px solid ${sendResult.type === 'success' ? '#86EFAC' : '#FECACA'}`,
            }}>
              {sendResult.text}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>To</label>
              <input type="email" placeholder="recipient@example.com"
                value={composeForm.to} onChange={(e) => setComposeForm(prev => ({ ...prev, to: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>Subject</label>
              <input type="text" placeholder="Email subject"
                value={composeForm.subject} onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>Body</label>
              <textarea rows={10} placeholder="Write your email..."
                value={composeForm.body} onChange={(e) => setComposeForm(prev => ({ ...prev, body: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleSend} disabled={sendLoading || !composeForm.to || !composeForm.subject || !composeForm.body}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 24px', fontSize: '14px' }}>
                <Send size={16} /> {sendLoading ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
      {ConfirmDialog}
    </div>
  );
}

// Helper components
function MetricCard({ label, value, sub, color }) {
  return (
    <div className="detail-card" style={{ padding: '16px', textAlign: 'center' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '13px', color: '#6b7280' }}>{label}</div>
      {sub && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    sent: { bg: '#DBEAFE', color: '#1E40AF' },
    delivered: { bg: '#DCFCE7', color: '#166534' },
    bounced: { bg: '#FEF2F2', color: '#991B1B' },
    complained: { bg: '#FEF3C7', color: '#92400E' },
    failed: { bg: '#FEE2E2', color: '#DC2626' },
  };
  const s = colors[status] || { bg: '#F3F4F6', color: '#6B7280' };
  return (
    <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: s.bg, color: s.color, fontWeight: 600 }}>
      {status || 'unknown'}
    </span>
  );
}

export default SESMailbox;
